import { useEffect, useRef, useState } from "react";
import { Badge, Button, Col, Overlay, Popover, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import { Formik, Form } from "formik";
import axios from "../../../apis/Api";
import storage from "../../../apis/Storage";
import { VzPageContainer } from "../../../layouts/VzPageContainer";
import SCRV2ReportListTable from "./SCRV2ReportListTable";
import FormikControl from "../../../layouts/Input/FormikControl";
import {
  formatDate,
  toCommaSeparated,
} from "../../../utilities/FUNCTIONS";
import {
  GET_DISTRIBUTOR_DDL,
  GET_ADDJOB_DISTRIBUTIONCENTER_DDL,
} from "../../../Types";
import { getDropdownList, getDropdownListByPost } from "../../Shared/dropdownListActions";
import {
  buildSCRV2SummaryPreview,
  exportSCRV2InvoiceWiseDetailList,
  getSCRV2ReportList,
  prefetchSCRV2SummaryReport,
} from "./SCRV2DetailReportActions";
import {
  formatScrV2DurationDays,
  renderScrV2IdentityDate,
} from "./SCRV2IdentityPresentation";

const MAX_DATE_RANGE_MONTHS = 3;
const allStatusOption = { label: "All", value: "All" };
const statusOptions = [
  allStatusOption,
  { label: "Open", value: "Open" },
  { label: "Finalized", value: "Finalized" },
];

const addMonths = (date, months) => {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
};

const startOfDay = (date) => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

const getToday = () => startOfDay(new Date());

const getMaxToDate = (fromDate) =>
  fromDate ? addMonths(startOfDay(fromDate), MAX_DATE_RANGE_MONTHS) : null;

const getMinFromDate = (toDate) =>
  toDate ? addMonths(startOfDay(toDate), -MAX_DATE_RANGE_MONTHS) : null;

const getMaxFromDate = (toDate) => {
  const latestAllowed = getToday();
  if (!toDate) {
    return latestAllowed;
  }

  const normalizedToDate = startOfDay(toDate);
  return normalizedToDate < latestAllowed ? normalizedToDate : latestAllowed;
};

const getMaxSelectableToDate = (fromDate = null) => {
  const latestAllowed = getToday();
  const rangeMax = fromDate ? getMaxToDate(fromDate) : null;

  if (!rangeMax || rangeMax > latestAllowed) {
    return latestAllowed;
  }

  return rangeMax;
};

const normalizeDateRange = (fromDate, toDate, changedField = null) => {
  let from = fromDate ? new Date(fromDate) : null;
  let to = toDate ? new Date(toDate) : null;

  if (!from || !to) {
    return { fromDate: from || fromDate, toDate: to || toDate };
  }

  if (from > to) {
    if (changedField === "fromDate") {
      to = new Date(from);
    } else if (changedField === "toDate") {
      from = new Date(to);
    } else {
      to = new Date(from);
    }
  }

  const maxTo = addMonths(from, MAX_DATE_RANGE_MONTHS);
  if (to > maxTo) {
    if (changedField === "toDate") {
      from = addMonths(to, -MAX_DATE_RANGE_MONTHS);
    } else {
      to = maxTo;
    }
  }

  const maxSelectableTo = getToday();
  if (to > maxSelectableTo) {
    to = maxSelectableTo;
    if (from > to) {
      from = addMonths(to, -MAX_DATE_RANGE_MONTHS);
    }
  }

  return { fromDate: startOfDay(from), toDate: startOfDay(to) };
};

const createDefaultDates = () => {
  const toDate = getToday();
  const fromDate = new Date(toDate);
  return { fromDate, toDate };
};

const createDefaultFilter = () => {
  const { fromDate, toDate } = createDefaultDates();

  return {
    distributor: [],
    distCenter: [],
    deliveryManName: [],
    deliveryManTillNo: [],
    scrNo: "",
    fromDate,
    toDate,
    status: allStatusOption,
  };
};

const SCRV2DetailReport = (props) => {
  const userRights = JSON.parse(storage.get("_userRights"));
  const [currentPageNo, setCurrentPageNo] = useState(0);
  const [currentPageSize, setCurrentPageSize] = useState(5);
  const [searchText, setSearchText] = useState("");
  const [show, setShow] = useState(false);
  const [target, setTarget] = useState(null);
  const ref = useRef(null);
  const deliveryUsersCacheRef = useRef(new Map());
  const [filter, setFilter] = useState(createDefaultFilter);
  const [distributorList, setDistributorList] = useState([]);
  const [distCenterList, setDistCenterList] = useState([]);
  const [deliveryManNameList, setDeliveryManNameList] = useState([]);
  const [deliveryManTillNoList, setDeliveryManTillNoList] = useState([]);
  const [isExporting, setIsExporting] = useState(false);

  const reportList = props.reportList || {};

  const formRequestObject = (overrides = {}) => {
    const activeSearchText =
      overrides.searchText !== undefined ? overrides.searchText : searchText;
    const trimmedSearch = activeSearchText?.trim() || "";

    if (trimmedSearch) {
      return {
        searchText: trimmedSearch,
        pageNumber:
          overrides.pageNumber !== undefined
            ? overrides.pageNumber
            : currentPageNo + 1,
        pageSize:
          overrides.pageSize !== undefined ? overrides.pageSize : currentPageSize,
      };
    }

    const current = { ...filter, ...overrides };
    const status = current.status;

    return {
      fromDate: current.fromDate ? formatDate(current.fromDate) : null,
      toDate: current.toDate ? formatDate(current.toDate) : null,
      isFinalized:
        status?.value === "Finalized" ? true : status?.value === "Open" ? false : null,
      scrNo: current.scrNo?.trim() || null,
      distributorIds: toCommaSeparated(current.distributor),
      distCenterIds: toCommaSeparated(current.distCenter),
      dlmIds: toCommaSeparated(current.deliveryManName),
      deliveryQRCodes: toCommaSeparated(current.deliveryManTillNo),
      pageNumber:
        overrides.pageNumber !== undefined
          ? overrides.pageNumber
          : currentPageNo + 1,
      pageSize:
        overrides.pageSize !== undefined ? overrides.pageSize : currentPageSize,
      searchText: null,
    };
  };

  const formExportRequestObject = () => {
    const status = filter.status;

    return {
      fromDate: filter.fromDate ? formatDate(filter.fromDate) : null,
      toDate: filter.toDate ? formatDate(filter.toDate) : null,
      isFinalized:
        status?.value === "Finalized" ? true : status?.value === "Open" ? false : null,
      scrNo: filter.scrNo?.trim() || null,
      distributorIds: toCommaSeparated(filter.distributor),
      distCenterIds: toCommaSeparated(filter.distCenter),
      dlmIds: toCommaSeparated(filter.deliveryManName),
      deliveryQRCodes: toCommaSeparated(filter.deliveryManTillNo),
    };
  };

  const exportReportList = async () => {
    if (isExporting) {
      return;
    }

    setIsExporting(true);
    try {
      await exportSCRV2InvoiceWiseDetailList(formExportRequestObject());
    } finally {
      setIsExporting(false);
    }
  };

  const fetchReportData = (request = null) => {
    props.getSCRV2ReportList(request || formRequestObject());
  };

  const openFilterModal = (event) => {
    setShow(!show);
    setTarget(event.target);
  };

  const getCenterDeliveryUsers = (distCenterIds) => {
    if (!distCenterIds) {
      setDeliveryManNameList([]);
      setDeliveryManTillNoList([]);
      return;
    }

    const cachedUsers = deliveryUsersCacheRef.current.get(distCenterIds);
    if (cachedUsers) {
      setDeliveryManNameList(cachedUsers.names);
      setDeliveryManTillNoList(cachedUsers.tillNos);
      return;
    }

    axios
      .get(`api/DistCenter/GetCenterDeliveryUsersDDL/${distCenterIds}`)
      .then(({ data }) => {
        if (data?.response) {
          const names = data.response.map((item) => ({
            value: item.id,
            label: item.fullname,
          }));
          const tillNos = data.response.map((item) => ({
            value: item.qrCode,
            label: item.qrCode,
          }));
          deliveryUsersCacheRef.current.set(distCenterIds, { names, tillNos });
          setDeliveryManNameList(names);
          setDeliveryManTillNoList(tillNos);
        } else {
          setDeliveryManNameList([]);
          setDeliveryManTillNoList([]);
        }
      })
      .catch(() => {
        setDeliveryManNameList([]);
        setDeliveryManTillNoList([]);
      });
  };

  useEffect(() => {
    setCurrentPageNo(0);
    fetchReportData(formRequestObject({ pageNumber: 1 }));
    // Search is the only field that should auto-refresh; filters refresh from Apply.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  useEffect(() => {
    props.getDropdownList(
      `api/Distributor/GetDistributorsDDL`,
      GET_DISTRIBUTOR_DDL
    );
  }, []);

  useEffect(() => {
    if (userRights != null && userRights?.userType === "DistributorUser") {
      const scopedDistributors = props.allDistributorList.filter(
        (x) => x.value === userRights?.distributorId
      );
      if (scopedDistributors?.length > 0) {
        setFilter((prev) => ({
          ...prev,
          distributor: scopedDistributors.map((item) => ({
            label: item.label,
            value: item.value,
          })),
        }));
      }
      setDistributorList(
        scopedDistributors.map((item) => ({
          value: item.value,
          label: item.label,
        }))
      );
    } else if (userRights != null && userRights?.userType === "CoreAppUser") {
      const scopedDistributors = props.allDistributorList.filter((x) =>
        userRights?.distributorId.includes(x.value)
      );
      setDistributorList(
        scopedDistributors.map((item) => ({
          value: item.value,
          label: item.label,
        }))
      );
    } else {
      setDistributorList(
        props.allDistributorList.map((item) => ({
          value: item.value,
          label: item.label,
        }))
      );
    }
  }, [props.allDistributorList]);

  useEffect(() => {
    if (
      userRights != null &&
      (userRights?.userType === "DistributorUser" ||
        userRights?.userType === "CoreAppUser")
    ) {
      const scopedDistCenters = props.selectedAddJobDistCentersList.filter((x) =>
        userRights?.distCenterIds.includes(x.value)
      );
      setDistCenterList(
        scopedDistCenters.map((item) => ({
          value: item.value,
          label: item.label,
        }))
      );
    } else {
      setDistCenterList(
        props.selectedAddJobDistCentersList.map((item) => ({
          value: item.value,
          label: item.label,
        }))
      );
    }
  }, [props.selectedAddJobDistCentersList]);

  useEffect(() => {
    if (
      userRights != null &&
      (userRights?.userType === "DistributorUser" ||
        userRights?.userType === "CoreAppUser")
    ) {
      props.getDropdownListByPost(
        `api/DistCenter/GetDistCentersDDL`,
        { distributorIds: toCommaSeparated(userRights?.distributorId) },
        GET_ADDJOB_DISTRIBUTIONCENTER_DDL
      );
    }

    if (filter.distributor?.length > 0) {
      props.getDropdownListByPost(
        `api/DistCenter/GetDistCentersDDL`,
        { distributorIds: toCommaSeparated(filter.distributor) },
        GET_ADDJOB_DISTRIBUTIONCENTER_DDL
      );
    } else if (
      userRights == null ||
      userRights?.userType === "Administrator"
    ) {
      setDistCenterList([]);
    }
  }, [filter.distributor]);

  useEffect(() => {
    if (filter.distCenter?.length > 0) {
      getCenterDeliveryUsers(toCommaSeparated(filter.distCenter));
    } else {
      setDeliveryManNameList([]);
      setDeliveryManTillNoList([]);
    }
  }, [filter.distCenter]);

  const getData = (page) => {
    setCurrentPageNo(page);
    fetchReportData(formRequestObject({ pageNumber: page + 1 }));
  };

  const pageSizeChanged = (pageSize) => {
    setCurrentPageNo(0);
    setCurrentPageSize(pageSize);
    fetchReportData(formRequestObject({ pageNumber: 1, pageSize }));
  };

  const applyFilters = () => {
    const { fromDate, toDate } = normalizeDateRange(filter.fromDate, filter.toDate);
    const nextFilter = { ...filter, fromDate, toDate };

    setFilter(nextFilter);
    setCurrentPageNo(0);
    fetchReportData(
      formRequestObject({
        ...nextFilter,
        pageNumber: 1,
      })
    );
    setShow(false);
  };

  const resetFilters = (resetForm) => {
    const resetFilter = createDefaultFilter();

    if (userRights != null && userRights?.userType === "DistributorUser") {
      const scopedDistributors = props.allDistributorList.filter(
        (x) => x.value === userRights?.distributorId
      );
      if (scopedDistributors?.length > 0) {
        resetFilter.distributor = scopedDistributors.map((item) => ({
          label: item.label,
          value: item.value,
        }));
      }
    }

    resetForm({ values: resetFilter });
    setFilter(resetFilter);
    setDeliveryManNameList([]);
    setDeliveryManTillNoList([]);
    setCurrentPageNo(0);
    fetchReportData(formRequestObject({ ...resetFilter, pageNumber: 1 }));
  };

  const cancelFilters = () => {
    setShow(false);
    setTarget(null);
  };

  const showDistributorFilter =
    userRights == null ||
    userRights?.userType === "CoreAppUser" ||
    userRights?.userType === "Administrator";

  const columns = [
    {
      Header: "SCR No.",
      accessor: "scrNo",
      Cell: ({ value }) => (
        <span className="font-weight-bold text-primary text-nowrap">{value || "-"}</span>
      ),
    },
    {
      Header: "Distributor",
      accessor: "distributorName",
    },
    {
      Header: "Dist Center",
      accessor: "distCenterName",
    },
    {
      Header: "DLM",
      accessor: "dlmName",
      Cell: ({ row }) =>
        row.original.dlmName || row.original.dlmUserName || "-",
    },
    {
      Header: "Business Date",
      accessor: "businessDate",
      Cell: (row) => (
        <div style={{ width: "85px" }}>{renderScrV2IdentityDate(row.value)}</div>
      ),
    },
    {
      Header: "Finalized Date",
      accessor: "finalizedDate",
      Cell: (row) => (
        <div style={{ width: "85px" }}>{renderScrV2IdentityDate(row.value)}</div>
      ),
    },
    {
      Header: "Duration",
      accessor: "durationDays",
      Cell: (row) => formatScrV2DurationDays(row.value),
    },
    {
      Header: "Status",
      accessor: "isFinalized",
      Cell: ({ value }) => (
        <Badge bg={value ? "success" : "warning"}>
          {value ? "Finalized" : "Open"}
        </Badge>
      ),
    },
    {
      Header: "Actions",
      accessor: "scrId",
      Cell: ({ value, row }) => (
        <Link
          className="btn btn-sm btn-outline-primary text-nowrap"
          to={{
            pathname: `/SCRV2SummaryReport/${value}`,
            state: { preview: buildSCRV2SummaryPreview(row.original) },
          }}
          onMouseEnter={() => prefetchSCRV2SummaryReport(value)}
          onFocus={() => prefetchSCRV2SummaryReport(value)}
        >
          <i className="fas fa-file-alt mr-1" />
          View Summary
        </Link>
      ),
    },
  ];

  return (
    <div>
      <VzPageContainer title="SCR Detail Report" compact>
        <Row className="align-items-center mb-3">
          <Col>
            <h4 className="mb-1">
              <b>SCR Detail Report</b>
            </h4>
          </Col>
        </Row>

        <Row>
          <Col md={10} style={{ float: "right" }}>
            <div ref={ref}>
              <Button onClick={openFilterModal} className="m-3" variant="outline-primary">
                <i className="fa fa-filter" />
                Filters
              </Button>

              <Overlay
                show={show}
                target={target}
                placement="bottom"
                container={ref}
                containerPadding={20}
                width="430px"
                style={{ width: "430px" }}
              >
                <Popover
                  id="popover-contained"
                  style={{ maxWidth: "400px", width: "400px" }}
                >
                  <Popover.Header as="h3">Filters</Popover.Header>
                  <Popover.Body>
                    <Formik
                      enableReinitialize
                      initialValues={filter}
                      validator={() => ({})}
                      onSubmit={applyFilters}
                    >
                      {(formik) => (
                        <Form autoComplete="off">
                          <Row>
                            {showDistributorFilter ? (
                              <Col xs={12} md={12}>
                                <FormikControl
                                  formik={formik}
                                  control="react-select"
                                  label="Distributor"
                                  name="distributor"
                                  options={distributorList}
                                  isMulti={true}
                                  isClearable={true}
                                  isSearchable={true}
                                  handleOnChange={(value) => {
                                    setFilter({
                                      ...filter,
                                      distributor: value || [],
                                      distCenter: [],
                                      deliveryManName: [],
                                      deliveryManTillNo: [],
                                    });
                                  }}
                                  value={filter.distributor}
                                />
                              </Col>
                            ) : null}

                            <Col xs={12} md={12}>
                              <FormikControl
                                formik={formik}
                                control="react-select"
                                label="Distribution Center"
                                name="distCenter"
                                options={
                                  filter.distributor.length > 0 ||
                                    userRights?.userType === "DistributorUser" ||
                                    userRights?.userType === "CoreAppUser"
                                    ? distCenterList
                                    : []
                                }
                                isMulti={true}
                                isClearable={true}
                                isSearchable={true}
                                handleOnChange={(value) => {
                                  setFilter({
                                    ...filter,
                                    distCenter: value || [],
                                    deliveryManName: [],
                                    deliveryManTillNo: [],
                                  });
                                }}
                                value={filter.distCenter}
                              />
                            </Col>

                            <Col xs={12} md={12}>
                              <FormikControl
                                formik={formik}
                                control="react-select"
                                label="DLM Name"
                                name="deliveryManName"
                                options={
                                  filter.distCenter.length > 0
                                    ? deliveryManNameList
                                    : []
                                }
                                isMulti={true}
                                isClearable={true}
                                isSearchable={true}
                                handleOnChange={(value) => {
                                  setFilter({
                                    ...filter,
                                    deliveryManName: value || [],
                                    deliveryManTillNo: [],
                                  });
                                }}
                                value={filter.deliveryManName}
                              />
                            </Col>

                            <Col xs={12} md={12}>
                              <FormikControl
                                formik={formik}
                                control="react-select"
                                label="DLM Till No"
                                name="deliveryManTillNo"
                                options={
                                  filter.distCenter.length > 0
                                    ? deliveryManTillNoList
                                    : []
                                }
                                isMulti={true}
                                isClearable={true}
                                isSearchable={true}
                                handleOnChange={(value) => {
                                  setFilter({
                                    ...filter,
                                    deliveryManTillNo: value || [],
                                  });
                                }}
                                value={filter.deliveryManTillNo}
                              />
                            </Col>

                            <Col xs={12} md={12}>
                              <FormikControl
                                formik={formik}
                                control="input"
                                label="SCR No"
                                name="scrNo"
                                placeholder="Enter SCR No"
                                handleOnChange={(value) => {
                                  setFilter({
                                    ...filter,
                                    scrNo: value ?? "",
                                  });
                                }}
                                value={filter.scrNo}
                              />
                            </Col>

                            <Col xs={12} md={12}>
                              <FormikControl
                                formik={formik}
                                control="react-select"
                                label="Status"
                                name="status"
                                options={statusOptions}
                                isClearable={true}
                                isSearchable={true}
                                handleOnChange={(value) => {
                                  setFilter({
                                    ...filter,
                                    status: value || allStatusOption,
                                  });
                                }}
                                value={filter.status}
                              />
                            </Col>

                            <Row style={{ paddingRight: 0 }}>
                              <Col xs={12} md={6}>
                                <FormikControl
                                  formik={formik}
                                  control="date"
                                  type="fromDate"
                                  label="From Date"
                                  name="fromDate"
                                  handleOnChange={(value) => {
                                    const { fromDate, toDate } = normalizeDateRange(
                                      value,
                                      filter.toDate,
                                      "fromDate"
                                    );
                                    setFilter({ ...filter, fromDate, toDate });
                                    formik.setFieldValue("fromDate", fromDate);
                                    formik.setFieldValue("toDate", toDate);
                                  }}
                                  value={filter.fromDate}
                                  maxDate={getMaxFromDate(filter.toDate)}
                                  minDate={getMinFromDate(filter.toDate)}
                                />
                              </Col>
                              <Col xs={12} md={6}>
                                <FormikControl
                                  formik={formik}
                                  control="date"
                                  type="toDate"
                                  label="To Date"
                                  name="toDate"
                                  handleOnChange={(value) => {
                                    const { fromDate, toDate } = normalizeDateRange(
                                      filter.fromDate,
                                      value,
                                      "toDate"
                                    );
                                    setFilter({ ...filter, fromDate, toDate });
                                    formik.setFieldValue("fromDate", fromDate);
                                    formik.setFieldValue("toDate", toDate);
                                  }}
                                  value={filter.toDate}
                                  minDate={filter.fromDate}
                                  maxDate={getMaxSelectableToDate(filter.fromDate)}
                                />
                              </Col>
                            </Row>
                          </Row>
                          <div className="row" style={{ marginTop: 20 }}>
                            <div className="form-group">
                              <Link
                                onClick={() => resetFilters(formik.resetForm)}
                                to="#"
                                className="active"
                                style={{
                                  padding: "0px",
                                  marginTop: "10px",
                                  float: "left",
                                }}
                              >
                                Reset
                              </Link>
                              <Link
                                onClick={cancelFilters}
                                to="#"
                                className="active"
                                style={{
                                  padding: "0px",
                                  marginTop: "10px",
                                  float: "right",
                                }}
                              >
                                Cancel
                              </Link>
                              <div className="float-right">
                                <Button
                                  type="submit"
                                  className="btn btn-primary"
                                  style={{
                                    marginRight: "10px",
                                    float: "right",
                                  }}
                                >
                                  Apply
                                </Button>
                              </div>
                            </div>
                          </div>
                        </Form>
                      )}
                    </Formik>
                  </Popover.Body>
                </Popover>
              </Overlay>
            </div>
          </Col>

          <Col md={2} style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              onClick={exportReportList}
              className="m-3"
              variant="outline-primary"
              disabled={isExporting}
            >
              <i className={`fa ${isExporting ? "fa-spinner fa-spin" : "fa-download"} me-1`} />
              {isExporting ? "Exporting..." : "Export"}
            </Button>
          </Col>
        </Row>

        <Row>
          <Col xs={12} md={12}>
            <SCRV2ReportListTable
              data={reportList.items || []}
              columns={columns}
              goToPreviousPage={(val) => getData(val)}
              goToNextPage={(val) => getData(val)}
              totalRecords={reportList.totalRecords || 0}
              pageSizeChange={(size) => pageSizeChanged(size)}
              myPageSize={currentPageSize}
              pageNo={currentPageNo}
              searchChanged={setSearchText}
            />
          </Col>
        </Row>
      </VzPageContainer>
    </div>
  );
};

const mapStateToProps = (state) => ({
  reportList: state.SCRV2DetailReport?.list,
  allDistributorList: state.ddlList.allDistributorList
    ? state.ddlList.allDistributorList
    : [],
  selectedAddJobDistCentersList: state.ddlList.selectedAddJobDistCentersList
    ? state.ddlList.selectedAddJobDistCentersList
    : [],
});

export default connect(mapStateToProps, {
  getSCRV2ReportList,
  getDropdownList,
  getDropdownListByPost,
})(SCRV2DetailReport);
